import { sleep } from '@flowus/common/async';
import { DragLine } from '@flowus/common/components/dragline';
import { cx } from '@flowus/common/cx';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TransitionBox } from 'src/common/components/transition-box';
import { AiChatPage } from 'src/components/ai-chat';
import { ServicePanel } from 'src/components/service-center/service-panel';
import { minDefaultDrawerWidth, useDrawerViewWidth } from 'src/services/app-support/drawer-width';
import { useGetPageId } from 'src/utils/getPageId';
import { useIsMaster } from 'src/utils/right-utils';
import { PageResolvedDiscussions } from 'src/views/comments/page-resolved-discussions';
import { PageFeeds } from './page-feeds/page-feeds';
import { useDrawerOpenStateValue } from './shared';

const DrawerView: FC<{
  className?: string;
  show?: boolean;
}> = (props) => {
  const { className, children, show } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { drawerWidth, setDrawerWidth } = useDrawerViewWidth();
  const [fixedWidth, setFixedWidth] = useState(show);

  useEffect(() => {
    if (show) {
      void sleep(500).then(() => setFixedWidth(false));
    } else {
      setFixedWidth(true);
    }
  }, [show]);

  return (
    <TransitionBox
      className={cx(
        'bg-white1 border-l relative flex-shrink-0',
        !show && 'border-none opacity-0 pointer-events-none',
        className
      )}
      style={{ width: show ? drawerWidth : 0, maxWidth: '80vw' }}
      domRef={containerRef}
    >
      {show && (
        <DragLine
          className="-left-0.5"
          container={containerRef}
          max={window.innerWidth * 0.8}
          min={minDefaultDrawerWidth}
          onChange={setDrawerWidth}
        />
      )}
      <div className="h-full" style={{ width: fixedWidth ? drawerWidth : '100%' }}>
        {children}
      </div>
    </TransitionBox>
  );
};

export const Drawer: FC = () => {
  const drawerOpen = useDrawerOpenStateValue();
  const pageId = useGetPageId();
  const isMaster = useIsMaster();
  const loadList = useRef({
    feeds: false,
    discussions: false,
    service: false,
    chat: false,
  });

  const showType = useMemo(() => {
    if (
      (drawerOpen === 'page-feeds' && isMaster) ||
      (drawerOpen === 'page-feeds-right' && !isMaster)
    ) {
      loadList.current.feeds = true;
      return 'page-feeds';
    }

    if (
      (drawerOpen === 'page-resolved-discussions' && isMaster) ||
      (drawerOpen === 'page-resolved-discussions-right' && !isMaster)
    ) {
      loadList.current.discussions = true;
      return 'page-resolved-discussions';
    }

    if (
      (drawerOpen === 'service-center' && isMaster) ||
      (drawerOpen === 'service-center-right' && !isMaster)
    ) {
      loadList.current.service = true;
      return 'service-center';
    }

    if ((drawerOpen === 'ai-chat' && isMaster) || (drawerOpen === 'ai-chat-right' && !isMaster)) {
      loadList.current.chat = true;
      return 'ai-chat';
    }

    return 'none';
  }, [drawerOpen, isMaster]);

  return (
    <DrawerView show={showType !== 'none'}>
      {[
        {
          type: 'page-feeds',
          isLoaded: loadList.current.feeds,
          component: <PageFeeds />,
        },
        {
          type: 'page-resolved-discussions',
          isLoaded: loadList.current.discussions,
          component: <PageResolvedDiscussions pageId={pageId} />,
        },
        {
          type: 'service-center',
          isLoaded: loadList.current.service,
          component: <ServicePanel />,
        },
        {
          type: 'ai-chat',
          isLoaded: loadList.current.chat,
          component: <AiChatPage />,
        },
      ].map(({ type, isLoaded, component }) => (
        <div
          key={type}
          className="animate__animated animate__fadeIn h-full w-full"
          hidden={showType !== type}
        >
          {isLoaded && component}
        </div>
      ))}
    </DrawerView>
  );
};
